import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MARKETS_COPY } from "../../content/site";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import SectionHeader from "../../components/shared/SectionHeader";
import OpportunityCard from "../../components/shared/OpportunityCard";
import LeadForm from "../../components/shared/LeadForm";
import { api } from "../../lib/api";

export default function MarketPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const m = MARKETS_COPY[slug];
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    if (!m) return;
    api.get(`/public/opportunities?market=${encodeURIComponent(m.country)}`).then((r) => setOpps(r.data.slice(0, 6))).catch(() => {});
  }, [m]);

  useEffect(() => { if (slug === "ghana") navigate("/explore-ghana", { replace: true }); }, [slug, navigate]);

  if (!m) return (
    <div className="container-editorial py-32">
      <h1 className="font-editorial text-4xl">Market not found</h1>
      <Link to="/" className="link-underline mt-4 inline-block">Return home</Link>
    </div>
  );

  return (
    <div data-testid={`page-market-${slug}`}>
      <section className="relative -mt-24 h-[70vh] min-h-[480px] overflow-hidden bg-altura-espresso">
        <img src={m.hero} alt={m.name} className="absolute inset-0 h-full w-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 flex h-full items-end pt-24">
          <div className="container-editorial pb-20 text-altura-ivory">
            <Eyebrow className="text-altura-gold">{m.flag} {m.name}</Eyebrow>
            <EditorialHeading size="xl" className="mt-4 text-altura-ivory max-w-3xl">{m.name}.</EditorialHeading>
            <p className="mt-5 max-w-xl text-altura-ivory/85 text-lg leading-relaxed">{m.intro}</p>
          </div>
        </div>
      </section>

      <section className="bg-altura-ivory py-24">
        <div className="container-editorial grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeader eyebrow="Why investors explore this market" title={`What makes ${m.name} interesting.`} size="md" />
            <div className="mt-10 space-y-6">
              {m.why_points.map((p, i) => (
                <div key={p.title} className="border-l-2 border-altura-gold/40 pl-6" data-testid={`market-why-${i}`}>
                  <h3 className="font-editorial text-xl text-altura-espresso">{p.title}</h3>
                  <p className="mt-2 text-sm text-altura-charcoal/70 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Eyebrow>Key Locations</Eyebrow>
            <EditorialHeading size="md" className="mt-4">Where the market lives.</EditorialHeading>
            <GoldDivider className="mt-6" />
            <ul className="mt-6 space-y-3 text-sm">
              {m.key_areas.map((a) => (
                <li key={a} className="flex gap-3 text-altura-charcoal/80">
                  <span className="mt-2 h-1 w-4 bg-altura-gold shrink-0" /> {a}
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Eyebrow>Property Types</Eyebrow>
              <div className="mt-4 flex flex-wrap gap-2">
                {m.property_types.map((t) => (
                  <span key={t} className="badge-soft">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {opps.length > 0 && (
        <section className="bg-altura-beige/40 py-24">
          <div className="container-editorial">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <SectionHeader eyebrow="Featured Opportunities" title={`${m.name} portfolio highlights.`} size="md" />
              <Link to={`/opportunities?market=${encodeURIComponent(m.country)}`} className="link-underline text-sm text-altura-espresso">View all →</Link>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {opps.map((o) => <OpportunityCard key={o.id} opportunity={o} />)}
            </div>
          </div>
        </section>
      )}

      <section className="bg-altura-ivory py-24">
        <div className="container-editorial max-w-4xl">
          <SectionHeader eyebrow="FAQs" title={`Frequently asked about ${m.name}.`} />
          <div className="mt-10 space-y-5">
            {m.faqs.map((f, i) => (
              <details key={f.q} className="group border-b border-altura-espresso/15 pb-5" data-testid={`market-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-editorial text-lg text-altura-espresso">{f.q}</span>
                  <span className="text-altura-gold text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-altura-charcoal/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-10 text-xs text-altura-charcoal/50">
            Altura Global does not provide legal, tax, immigration, or financial advice. Property regulations, foreign-ownership rules, and tax treatment vary by market and individual circumstances. We coordinate with licensed local professionals on every transaction.
          </p>
        </div>
      </section>

      <section className="bg-altura-espresso-2 text-altura-ivory py-24">
        <div className="container-editorial grid lg:grid-cols-2 gap-14">
          <div>
            <Eyebrow className="text-altura-gold">Request Information</Eyebrow>
            <EditorialHeading size="lg" className="mt-4 text-altura-ivory">Begin your {m.name} conversation.</EditorialHeading>
            <p className="mt-5 text-altura-ivory/70 max-w-md">Tell us about your objectives. An Altura Global specialist will follow up.</p>
          </div>
          <div className="rounded-lg border border-altura-ivory/10 bg-altura-ivory/5 p-8">
            <LeadForm defaultMarket={m.country} sourcePage={`/markets/${slug}`} testIdPrefix={`market-${slug}-inquiry`} />
          </div>
        </div>
      </section>
    </div>
  );
}
